import produce, { Draft } from "immer";
import Debug from "debug";
import { v4 as uuidv4 } from "uuid";

import {
  EditorStore,
  Stream,
  ActionChannelGetOneSuccess,
  ActionLoading,
  ActionChannel,
  ActionChannelDeleteOne,
  PayloadChannelDeleteOne,
  ActionChannelEditOne,
  PayloadChannelEditOne,
  ActionEditorAuthGetSuccess,
  PayloadEditorAuthGetSuccess,
  AuthMode,
  EditorError,
  ActionError,
} from "../types";
import { ACTIONS } from "../actions/constants";

const debug = Debug("broadcast:reducers:editor");

const processChannel = (
  editor: EditorStore,
  draft: Draft<EditorStore>,
  payload: Stream
): void => {
  debug("processChannel", payload);

  if (payload === null || payload.channelDeleted) {
    draft.channel = null;
  } else {
    draft.channel = payload;
  }
  draft.configChannelConfirmation = true;
  draft.loading = !draft.requestAllChannelsSuccess;
  draft.createLoading = false;
};

const processChannelError = (
  editor: EditorStore,
  draft: Draft<EditorStore>
): void => {
  debug("processChannelError");

  draft.configChannelConfirmation = true;
  draft.loading = !draft.requestAllChannelsSuccess;
  draft.createLoading = false;
};

const selectChannel = (
  editor: EditorStore,
  draft: Draft<EditorStore>,
  payload: Stream | null
): void => {
  debug("processChannel", payload);
  draft.channel = payload;
};

const showChannel = (
  editor: EditorStore,
  draft: Draft<EditorStore>,
  payload: boolean
): void => {
  debug("showChannel", payload);
  draft.showChannel = payload;
};

const setDeleteChannel = (
  editor: EditorStore,
  draft: Draft<EditorStore>,
  payload: Stream | null
): void => {
  debug("setDeleteChannel", payload);
  draft.deleteChannel = payload;
};

const setRenameChannel = (
  editor: EditorStore,
  draft: Draft<EditorStore>,
  payload: Stream | null
): void => {
  debug("setRenameChannel", payload);
  draft.renameChannel = payload;
};

const loading = (
  editor: EditorStore,
  draft: Draft<EditorStore>,
  payload: boolean
): void => {
  debug("loading", payload);
  draft.loading = payload && !draft.requestAllChannelsSuccess;
};

const createLoading = (
  editor: EditorStore,
  draft: Draft<EditorStore>,
  payload: boolean
): void => {
  debug("createLoading", payload);
  draft.createLoading = payload;
};

const requestAllChannelsSuccess = (
  editor: EditorStore,
  draft: Draft<EditorStore>,
  payload: boolean
): void => {
  debug("requestAllChannelsSuccess", payload);
  draft.requestAllChannelsSuccess = true;
  draft.loading = !draft.configChannelConfirmation;
};

const configChannelPresentConfirmation = (
  editor: EditorStore,
  draft: Draft<EditorStore>,
  payload: boolean
): void => {
  debug("configChannelPresentConfirmationng", payload);
  draft.configChannelConfirmation = payload;
};

const actionInProgress = (
  editor: EditorStore,
  draft: Draft<EditorStore>,
  payload: string | null
): void => {
  debug("actionInProgress", payload);
  draft.actionInProgress = payload;
};

const setCreation = (
  editor: EditorStore,
  draft: Draft<EditorStore>,
  payload: boolean
): void => {
  debug("setCreation", payload);
  draft.createChannel = payload;
};

const updateChannelAuth = (
  editor: EditorStore,
  draft: Draft<EditorStore>,
  payload: PayloadEditorAuthGetSuccess
): void => {
  debug("updateChannelAuth", payload);
  draft.editorAuthenticating = false;

  const { user } = payload;
  const { team, authProvider } = user;
  const { id: teamId, name: teamName } = team;

  if (draft.channel) {
    draft.channel.orgId = teamId;
    draft.channel.orgName = teamName;
    draft.channel.password = "";
    draft.channel.hasPassword = false;
    draft.channel.authMode =
      authProvider.toString() === AuthMode.Slack.toString()
        ? AuthMode.Slack
        : AuthMode.Teams;
  }
};

const setEditorAuthenticating = (
  editor: EditorStore,
  draft: Draft<EditorStore>,
  payload: boolean
): void => {
  draft.editorAuthenticating = payload;
};

const processChannelEdit = (
  editor: EditorStore,
  draft: Draft<EditorStore>,
  payload: Stream
): void => {
  debug("processChannelEdit", payload);

  draft.channel = payload;
  draft.editorAuthenticating = false;
};

const addError = (
  editor: EditorStore,
  draft: Draft<EditorStore>,
  payload: EditorError
): void => {
  debug("addError", payload);

  const errorId = uuidv4();
  const sameErrors = draft.errors.filter(
    (error) => error.error === payload.error
  );
  const differentErrors = draft.errors.filter(
    (error) => error.error !== payload.error
  );
  debug("same errors", sameErrors);
  draft.errors = [...differentErrors, { uuid: errorId, error: payload.error }];
};

const removeError = (
  editor: EditorStore,
  draft: Draft<EditorStore>,
  payload: EditorError
): void => {
  debug("removeError", payload);
  const { uuid } = payload;
  draft.errors = draft.errors.filter((error) => error.uuid !== uuid);
};

const tokenFailure = (editor: EditorStore, draft: Draft<EditorStore>): void => {
  debug("tokenFailure");

  draft.loading = false;
  addError(editor, draft, { error: "Unable to get Studio Token" });
};

export const editorReducer = (
  editor = {
    channel: null,
    showChannel: false,
    createChannel: false,
    deleteChannel: null,
    renameChannel: null,
    loading: true,
    createLoading: false,
    requestAllChannelsSuccess: false,
    configChannelConfirmation: false,
    actionInProgress: null,
    editorAuthenticating: false,
    errors: [],
  },
  {
    type,
    payload,
  }:
    | ActionChannelGetOneSuccess
    | ActionLoading
    | ActionChannel
    | ActionChannelDeleteOne
    | ActionChannelEditOne
    | ActionEditorAuthGetSuccess
    | ActionError
): EditorStore =>
  produce(editor, (draft) => {
    switch (type) {
      case ACTIONS.CHANNELS_GET_ALL_SUCCESS: {
        requestAllChannelsSuccess(editor, draft, true);
        break;
      }
      case ACTIONS.CHANNELS_GET_ONE_SUCCESS:
      case ACTIONS.CHANNELS_CREATE_ONE_SUCCESS: {
        processChannel(editor, draft, payload as Stream);
        break;
      }
      case ACTIONS.EDITOR_CONFIG_CHANNEL_PRESENT: {
        configChannelPresentConfirmation(editor, draft, payload as boolean);
        break;
      }
      case ACTIONS.EDITOR_CONFIG_CHANNEL_SHOW: {
        showChannel(editor, draft, true);
        break;
      }
      case ACTIONS.EDITOR_CONFIG_CHANNEL_HIDE: {
        showChannel(editor, draft, false);
        break;
      }
      case ACTIONS.EDITOR_DELETE_WARNING_MODAL_SHOW: {
        setDeleteChannel(editor, draft, payload as Stream);
        break;
      }
      case ACTIONS.EDITOR_DELETE_WARNING_MODAL_HIDE: {
        setDeleteChannel(editor, draft, null);
        break;
      }
      case ACTIONS.CHANNELS_DELETE_ONE_REQUEST: {
        const { channelId } = payload as PayloadChannelDeleteOne;
        actionInProgress(editor, draft, channelId);
        break;
      }
      case ACTIONS.CHANNELS_DELETE_ONE_SUCCESS: {
        actionInProgress(editor, draft, null);
        break;
      }
      case ACTIONS.CHANNELS_DELETE_ONE_FAILURE: {
        actionInProgress(editor, draft, null);
        addError(editor, draft, payload as EditorError);
        break;
      }
      case ACTIONS.CHANNELS_EDIT_ONE_REQUEST: {
        const { channelId } = payload as PayloadChannelEditOne;
        actionInProgress(editor, draft, channelId);
        break;
      }
      case ACTIONS.CHANNELS_EDIT_ONE_SUCCESS: {
        actionInProgress(editor, draft, null);
        break;
      }
      case ACTIONS.CHANNELS_EDIT_ONE_FAILURE: {
        actionInProgress(editor, draft, null);
        addError(editor, draft, payload as EditorError);
        break;
      }
      case ACTIONS.EDITOR_RENAME_MODAL_SHOW: {
        setRenameChannel(editor, draft, payload as Stream);
        break;
      }
      case ACTIONS.EDITOR_RENAME_MODAL_HIDE: {
        setRenameChannel(editor, draft, null);
        break;
      }
      case ACTIONS.EDITOR_LOADING_STATE: {
        loading(editor, draft, payload as boolean);
        break;
      }
      case ACTIONS.EDITOR_SELECT_CHANNEL: {
        selectChannel(editor, draft, payload as Stream);
        break;
      }
      case ACTIONS.EDITOR_DESELECT_CHANNEL: {
        selectChannel(editor, draft, null);
        break;
      }
      case ACTIONS.EDITOR_CREATE_MODAL_SHOW: {
        setCreation(editor, draft, true);
        break;
      }
      case ACTIONS.EDITOR_CREATE_MODAL_HIDE: {
        setCreation(editor, draft, false);
        break;
      }
      case ACTIONS.CHANNELS_CREATE_ONE_REQUEST: {
        createLoading(editor, draft, true);
        break;
      }
      case ACTIONS.CHANNELS_CREATE_ONE_FAILURE: {
        createLoading(editor, draft, false);
        addError(editor, draft, payload as EditorError);
        break;
      }
      case ACTIONS.EDITOR_AUTH_SUCCESS: {
        updateChannelAuth(
          editor,
          draft,
          payload as PayloadEditorAuthGetSuccess
        );
        break;
      }
      case ACTIONS.EDITOR_AUTH_REQUEST:
      case ACTIONS.EDITOR_AUTH_REMOVE_REQUEST:
      case ACTIONS.EDITOR_PASSWORD_AUTH_REQUEST:
      case ACTIONS.EDITOR_PUBLIC_PRIVATE_AUTH_REQUEST: {
        setEditorAuthenticating(editor, draft, true);
        break;
      }
      case ACTIONS.EDITOR_AUTH_FAILURE:
      case ACTIONS.EDITOR_AUTH_REMOVE_FAILURE:
      case ACTIONS.EDITOR_PASSWORD_AUTH_FAILURE:
      case ACTIONS.EDITOR_PUBLIC_PRIVATE_AUTH_FAILURE: {
        setEditorAuthenticating(editor, draft, false);
        addError(editor, draft, payload as EditorError);
        break;
      }
      case ACTIONS.EDITOR_AUTH_REMOVE_SUCCESS:
      case ACTIONS.EDITOR_PASSWORD_AUTH_SUCCESS:
      case ACTIONS.EDITOR_PUBLIC_PRIVATE_AUTH_SUCCESS: {
        processChannelEdit(editor, draft, payload as Stream);
        break;
      }
      case ACTIONS.CHANNELS_GET_ALL_FAILURE:
      case ACTIONS.BROADCAST_SETTINGS_FAILURE: {
        addError(editor, draft, payload as EditorError);
        break;
      }
      case ACTIONS.CHANNELS_GET_ONE_FAILURE: {
        processChannelError(editor, draft);
        addError(editor, draft, payload as EditorError);
        break;
      }
      case ACTIONS.EDITOR_REMOVE_ERROR_CONFIRM: {
        removeError(editor, draft, payload as EditorError);
        break;
      }
      case ACTIONS.STUDIO_TOKEN_FAILURE: {
        tokenFailure(editor, draft);
        break;
      }
      default:
        break;
    }
  });
